import { useEffect, useState } from "react";
import DataTable from "../../../components/table/data/DataTable";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UserService from "../../../services/user/UserService";
import IUser from "../../../models/user";
import { useUserColumns } from "../../../hooks/useTableColumns";
import { loadUsersWithLastSessionDates } from "./UserEditPage";
import { ERole } from "../../../models/enums/role";
import Toolbar from "../../../components/toolbar/Toolbar";
import DownloadTable from "../../../utils/download/downloadDataTable";
import { IFilter } from "../../../components/table/data/filter/DataFilter";
import { EFilterType } from "../../../components/table/data/filter/filter";
import { useLoading } from "../../../hooks/useLoadingContext";

export interface IUserDetails extends IUser {
    firstName: string;
    lastName: string;
    lastSessionDate?: Date;
}

const UserPage = () => {
    const columns = useUserColumns();
    const navigate = useNavigate();
    const [isFilterActive, setIsFilterActive] = useState<boolean>(true);
    const [usersDetails, setUsersDetails] = useState<IUserDetails[]>([]);
    const { t } = useTranslation();

    const [filteredRows, setFilteredRows] = useState(usersDetails);
    const [selectedFilters, setSelectedFilters] = useState<
        Map<string, IFilter>
    >(new Map());

    const visibleFilters: string[] = [];
    selectedFilters.forEach((filter, field) => {
        if (!filter.isActive || !filter.request) return;
        const column = columns.find((column) => column.field === field);
        visibleFilters.push(`${column?.headerName}: ${t(`periodical:filter.${filter.type}`)} ${filter.request}`);
    });

    const downloadTable = new DownloadTable("user", filteredRows, columns, t("table.user.title"), visibleFilters)

    const getUsers = async () => {
        let users = await UserService.GetUsersByCompany();
        users = users.filter((user) => user.role !== ERole.SuperAdmin);

        const usersWithLastSessionDates = await loadUsersWithLastSessionDates(users);
        if (usersWithLastSessionDates) setUsersDetails(usersWithLastSessionDates);

    }

    const { setBypassPaths } = useLoading();

    useEffect(() => {
        setBypassPaths(['/GetRawScansByUser']);

        return () => setBypassPaths([]);
    }, []);

    // setBypassPaths([ '/GetRawScansByUser', '/GetUserNotificationsByUserId', '/GetUsersByCompany'])
    useEffect(() => {
        getUsers();
    }, []);

    return (
        <Box sx={{ width: "100%", height: "100%" }}>
            <Toolbar
                onFilterClick={() => setIsFilterActive((old) => !old)}
                onCreate={() => navigate("add")}
                isFilterActive={isFilterActive}
                downloadButtons={[
                    { label: "Excel", onClick: () => downloadTable.GetExcel(t("header.exportedBy"), t("header.exportDate")) },
                    { label: "CSV", onClick: () => downloadTable.GetCSV() },
                    { label: "PDF", onClick: () => downloadTable.GetPDF(t("header.exportedBy"), t("header.exportDate")) }
                ]}
            />

            <DataTable
                dense
                disableFiltrer={!isFilterActive}
                rows={usersDetails}
                columns={columns}
                onRowClick={(row) =>
                    navigate("edit", { state: { ...row } })
                }
                getRowNavTo={(row) => "edit"}
                onSetFilter={(filteredRows, selectedFilters) => {
                    setFilteredRows(filteredRows)
                    setSelectedFilters(selectedFilters)
                }}
                initialFilters={new Map<string, IFilter>([["isLocked", {
                    field: "isLocked",
                    isActive: true,
                    request: t("table.user.active"),
                    type: EFilterType.Is,
                    isCaseSensitive: true
                }]])}
            />
        </Box>
    );
};
export default UserPage;
